"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getMyEffectivePermissions } from "@/lib/api/endpoints/permissions";
import { setRouteFlash } from "@/lib/navigation/route-flash";
import { NO_ACCESS_ROUTE, resolveAccessibleRoute } from "@/lib/navigation/sidebar-nav";
import { getCurrentUser } from "@/modules/auth/api";
import { useAuthStoreHydrated } from "@/modules/auth/hooks";
import { LoginForm } from "@/src/login/login-form";

/** Query keys that must never persist in the location bar (PII / secrets). */
const SENSITIVE_QUERY_KEYS = new Set([
  "password",
  "passwd",
  "pwd",
  "pass",
  "secret",
  "token",
  "email",
  "authorization",
]);

type LoginClientProps = {
  nextPath: string;
  authErrorFromRedirect?: boolean;
};

export function LoginClient({ nextPath, authErrorFromRedirect }: LoginClientProps) {
  const navigate = useNavigate();
  const hydrated = useAuthStoreHydrated();
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const url = new URL(window.location.href);
    const keys = [...url.searchParams.keys()];
    let changed = false;
    for (const key of keys) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    const qs = url.searchParams.toString();
    const path = url.pathname;
    window.history.replaceState({}, "", qs ? `${path}?${qs}` : path);
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    let cancelled = false;
    const ac = new AbortController();
    setCheckingSession(true);

    async function resolveDestination() {
      const currentUser = await getCurrentUser(ac.signal);

      if (cancelled) {
        return;
      }

      if (!currentUser) {
        setCheckingSession(false);
        return;
      }

      let destination = nextPath;
      try {
        const permissions = await getMyEffectivePermissions();
        const permissionMap = permissions.modules.reduce<Record<string, (typeof permissions.modules)[number]>>((acc, module) => {
          acc[module.module_key] = module;
          return acc;
        }, {});
        destination = resolveAccessibleRoute(currentUser, permissionMap, nextPath);
        if (destination === NO_ACCESS_ROUTE) {
          setRouteFlash("Your account is active, but no module access has been assigned yet.");
        } else if (destination !== nextPath) {
          setRouteFlash("You are already signed in. Redirected to an available module.");
        }
      } catch {
        destination = resolveAccessibleRoute(currentUser, undefined, nextPath);
      }

      navigate(destination, { replace: true });
    }

    void resolveDestination().catch(() => {
      if (!cancelled) {
        setCheckingSession(false);
      }
    });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [hydrated, navigate, nextPath]);

  if (checkingSession) {
    return (
      <main
        className="relative flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-sm text-muted"
        style={{ minHeight: "100dvh" }}
      >
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent/25 border-t-accent" aria-hidden />
        Establishing secure session…
      </main>
    );
  }

  return <LoginForm nextPath={nextPath} authErrorFromRedirect={authErrorFromRedirect} />;
}
